/**
 * Demo identities and keys.
 *
 * Dependency-free on purpose: `session.ts` and the two shims import this, and
 * they are eager (they sit in the main bundle). Nothing here may pull in the
 * generator or the query engine.
 */

/** Fixed, valid-shaped UUIDs. Never `crypto.randomUUID()` — the demo must be
 *  byte-identical on every load so Playwright assertions stay stable. */
export const demoUuid = (namespace: number, index: number): string => {
  const a = namespace.toString(16).padStart(8, '0');
  const b = index.toString(16).padStart(12, '0');
  return `${a}-0000-4000-8000-${b}`;
};

/** Namespaces, so ids from different tables can never collide. */
export const NS = {
  person: 1,
  ride: 2,
  negotiation: 3,
  offer: 4,
  payment: 5,
  vehicle: 6,
  rating: 7,
  /**
   * Rows a visitor creates through a plain `.from().insert()`, which Postgres
   * would have given a `gen_random_uuid()` default. Kept in its own namespace
   * so a session's writes can never collide with seeded ids.
   */
  written: 8,
} as const;

/** Maya Chen — the rider persona the visitor plays on the rider track. */
export const RIDER_ID = demoUuid(NS.person, 1);
/** Andre Wallace — the driver persona the visitor plays on the driver track. */
export const DRIVER_ID = demoUuid(NS.person, 2);

export const DEMO_ROLE_KEY = 'parleyride_demo_role';
export const DEMO_STORE_KEY = 'parleyride_demo_data';
/**
 * Namespace for anything a demo session writes that is NOT part of the store —
 * today just the rider's recent locations, which the shared hook keys itself.
 * Prefixing keeps them out of the real app's storage in both directions.
 */
export const DEMO_RECENT_LOCATIONS_PREFIX = 'parleyride_demo:';

/**
 * Bump to invalidate a store already sitting in a visitor's sessionStorage
 * after the generator's shape changes.
 */
export const SEED_VERSION = 1;

export type DemoRole = 'rider' | 'driver';

/** Who `auth.uid()` resolves to for a given track. */
export const actorIdFor = (role: DemoRole): string =>
  role === 'driver' ? DRIVER_ID : RIDER_ID;
